import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { customers as frCustomers } from '../../../../../locales/fr/customers';
import { customers as esCustomers } from '../../../../../locales/es/customers';
import { customers as arCustomers } from '../../../../../locales/ar/customers';
import useFormNavigation from '../../../../../hooks/useFormNavigation';
import FloatingAlert from '../../../../../components/Common/FloatingAlert';

const translations = {
  fr: frCustomers,
  es: esCustomers,
  ar: arCustomers
};

const CustomerForm = ({ customer, isEditMode, onSubmit, onCancel, isSubmitting, alert, onAlertClose, onResetToCreate }) => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const { handleFormKeyDown } = useFormNavigation();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    notes: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
        isActive: customer.isActive ?? true
      });
    }
  }, [customer]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t.form.validations.required;
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t.form.validations.required;
    } else if (!/^\+?[\d\s-]{8,}$/.test(formData.phone)) {
      newErrors.phone = t.form.validations.phone;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseFloat(value) || 0 : 
              value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      onKeyDown={handleFormKeyDown}
      className="space-y-4"
    >
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          {t.form.fields.name}
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={t.form.placeholders.name}
          className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
            errors.name ? 'input-error' : ''
          }`}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          {t.form.fields.phone}
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder={t.form.placeholders.phone}
          className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
            errors.phone ? 'input-error' : ''
          }`}
        />
        {errors.phone && (
          <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
        )}
      </div>

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700">
          {t.form.fields.address}
        </label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder={t.form.placeholders.address}
          rows={3}
          className="textarea textarea-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
        />
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
          {t.form.fields.notes}
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder={t.form.placeholders.notes}
          rows={3}
          className="textarea textarea-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
        />
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-700">
            {t.form.fields.solde}: {customer?.solde?.toFixed(2) || '0.00'} Dh
          </span>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="checkbox checkbox-primary"
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
            {t.form.fields.isActive}
          </label>
        </div>
      </div>

      <div className="flex justify-between items-center pt-4">
        <div className="flex space-x-2">
          {isEditMode && (
            <button
              type="button"
              onClick={() => {
                // Réinitialiser le formulaire pour un nouveau client
                setFormData({
                  name: '',
                  phone: '',
                  address: '',
                  notes: '',
                  isActive: true
                });
                setErrors({});
                
                // Masquer l'alert s'il y en a un
                if (onAlertClose) {
                  onAlertClose();
                }
                
                // Passer en mode création
                if (onResetToCreate) {
                  onResetToCreate();
                }
              }}
              className="btn btn-secondary btn-sm"
              disabled={isSubmitting}
            >
              <span className="text-lg">+</span>
              {/* <span className="ml-1">Nouveau client</span> */}
            </button>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-ghost"
          >
            {t.form.cancel}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? t.form.submitting : (isEditMode ? t.form.update : t.form.create)}
          </button>
        </div>
      </div>

      {/* FloatingAlert pour les messages de succès/erreur */}
      <FloatingAlert
        open={alert?.open || false}
        onClose={onAlertClose}
        type={alert?.type || 'info'}
      >
        {alert?.message || ''}
      </FloatingAlert>
    </form>
  );
};

export default CustomerForm; 